import React from 'react'
import { useSelector } from 'react-redux'

import { Account } from '@driftt/api-types'
import { StatusBadge } from '@driftt/dds'

import AccountFitTooltip from 'components/AccountFitTooltip'
import { useAppSelector } from 'hooks/useRedux'
import { CompanyData, ScoreBreakdown } from 'modules/accountActivity/domain'
import { getCurrentOrg } from 'selectors'
import { ACCOUNT_FIT_BUCKETS, bucketEngagementScore, ENGAGEMENT_BUCKETS } from 'utils/bucketingUtil'
import { isAccountFitEnabled } from 'utils/features'

import AccountInfoItem from './AccountInfoItem'

interface AccountInfoRowProps {
  account: Account | undefined
  company: CompanyData | undefined
  accountFitScore: number | undefined
  scoreBreakdown: ScoreBreakdown | undefined
  numOfOpportunities: any
  accountEngagement: number | undefined
  showEngagementScore: boolean
  maxItemWidth?: number
}

const AccountInfoRow = ({
  account,
  company,
  accountFitScore,
  scoreBreakdown,
  numOfOpportunities,
  accountEngagement,
  showEngagementScore,
  maxItemWidth = 300
}: AccountInfoRowProps) => {
  const org = useSelector(getCurrentOrg)
  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)

  const showAccountFitScore = isAccountFitEnabled(org)

  const chooseBucketVariant = (bucket: string) => {
    switch (bucket) {
      case ACCOUNT_FIT_BUCKETS.EXCELLENT:
      case ACCOUNT_FIT_BUCKETS.GOOD:
      case ENGAGEMENT_BUCKETS.EXCELLENT:
      case ENGAGEMENT_BUCKETS.GOOD:
        return (
          <StatusBadge variant="success" size="medium">
            {bucket}
          </StatusBadge>
        )
      case ACCOUNT_FIT_BUCKETS.FAIR:
      case ENGAGEMENT_BUCKETS.FAIR:
        return (
          <StatusBadge variant="warning" size="medium">
            {bucket}
          </StatusBadge>
        )
      case ACCOUNT_FIT_BUCKETS.POOR:
      case ENGAGEMENT_BUCKETS.POOR:
        return (
          <StatusBadge variant="error" size="medium">
            {bucket}
          </StatusBadge>
        )
      default:
        return ''
    }
  }

  return (
    <>
      {showAccountFitScore && (
        <AccountInfoItem
          label="Account fit"
          validation={!!accountFitScore}
          property={
            <AccountFitTooltip
              companyData={company}
              accountFitScore={accountFitScore}
              scoreBreakdown={scoreBreakdown}
            />
          }
          maxWidth={maxItemWidth}
        />
      )}
      <AccountInfoItem
        label="Industry"
        validation={!!company?.industry}
        property={company?.industry ?? ''}
        maxWidth={maxItemWidth}
      />
      <AccountInfoItem
        label="Employee count range"
        validation={!!company?.employeeCountRange}
        property={company?.employeeCountRange ?? ''}
        maxWidth={maxItemWidth}
      />
      <AccountInfoItem
        label="Location"
        validation={!!company?.city}
        property={`${company?.city}${', ' + company?.state}${', ' + company?.country}`}
        maxWidth={maxItemWidth}
      />
      {showEngagementScore && (
        <AccountInfoItem
          label="Account engagement"
          validation={!!accountEngagement}
          property={chooseBucketVariant(bucketEngagementScore(accountEngagement))}
          maxWidth={maxItemWidth}
        />
      )}
      <AccountInfoItem
        label="Estimated annual revenue"
        validation={!!company?.revenue}
        property={company?.revenue ?? ''}
        maxWidth={maxItemWidth}
      />
      <AccountInfoItem
        label="Account owner"
        validation={!!account?.ownerId && !!orgUsersById[account?.ownerId]}
        property={orgUsersById[account?.ownerId ?? 0]?.name ?? ''}
        maxWidth={maxItemWidth}
      />
      <AccountInfoItem
        label="Opportunities"
        validation={!!numOfOpportunities}
        property={numOfOpportunities}
        maxWidth={maxItemWidth}
      />
    </>
  )
}

export default AccountInfoRow
